<!-- src/components/PasswordInput.vue -->
<template>
  <div class="password-wrapper" ref="passwordWrapper">
    <!-- Centered Logo with same typography as main long read -->
    <h1 class="boldFont homepage-title alignCentre">
      The <span class="serif title-lower">33 Path</span>
    </h1>
    <div class="column alignCentreColumn">
      <!-- Landing Page NDA -->
      <MicroNDA variant="landing" />

      <div class="request-button-container">
        <button class="request-access-button" @click="showRequestDrawer = true">
          Request Access
        </button>
      </div>

      <!-- Intro Note for visitors -->
      <p class="intro-note">
        This private presentation is reserved for potential investors, executive
        producers, and institutional partners.
      </p>
      <p class="intro-note">
        If you wish to explore introductory context before requesting access,
        visit
        <a
          href="https://instagram.com/the33path"
          target="_blank"
          rel="noopener noreferrer"
          class="instagram-link"
          >@the33path</a
        >
        on Instagram.
      </p>
    </div>

    <!-- Password Input Section -->
    <div class="password-container">
      <!-- Animated Error Message -->
      <transition name="fade">
        <p v-if="error" class="error-message" role="alert">{{ error }}</p>
      </transition>

      <div class="column alignCentreColumn">
        <!-- Request Access Button at the top of the column -->

        <div class="password-field">
          <input
            :type="passwordVisible ? 'text' : 'password'"
            v-model="password"
            @keyup.enter="checkPassword"
            placeholder="Enter Password"
            class="password-input"
            ref="passwordInput"
            aria-label="Password Input"
          />
          <button type="button" class="toggle-btn" @click="togglePassword">
            <svg class="password-icon icon">
              <use :href="passwordVisible ? hideIconPath : viewIconPath" />
            </svg>
          </button>
        </div>
        <button
          class="submit-button"
          @click="checkPassword"
          aria-label="Submit Password"
          :disabled="isFadingOut"
        >
          Submit
        </button>
        <!-- Experience Note -->
        <small class="experience-note">
          For a better experience, make sure you watch this on a computer and
          turn off the lights
        </small>
      </div>
    </div>

    <!-- Copyright Footer -->
    <CopyrightFooter class="password-footer" />

    <!-- Bottom Drawer for Request Form -->
    <BottomDrawer
      :isOpen="showRequestDrawer"
      @closeDrawer="showRequestDrawer = false"
    >
      <RequestAccessForm
        @requestSubmitted="onRequestSubmitted"
        @closeDrawer="showRequestDrawer = false"
      />
    </BottomDrawer>
  </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import BottomDrawer from "@/components/GenericComponents/BottomDrawer.vue";
import RequestAccessForm from "@/components/GenericComponents/RequestAccessForm.vue";
import MicroNDA from "@/components/GenericComponents/MicroNDA.vue";
import CopyrightFooter from "@/components/GenericComponents/CopyrightFooter.vue";

const viewIconPath = require("@/assets/iconset.svg") + "#view";
const hideIconPath = require("@/assets/iconset.svg") + "#hide";

export default {
  name: "PasswordInput",
  components: {
    BottomDrawer,
    RequestAccessForm,
    MicroNDA,
    CopyrightFooter,
  },
  emits: ["authenticated"], // Declare the 'authenticated' event
  setup(_, { emit }) {
    const password = ref("");
    const correctPassword = "projector33"; // Replace with your actual password
    const passwordInput = ref(null);
    const passwordWrapper = ref(null); // Ref to the entire component wrapper
    const error = ref("");
    const isFadingOut = ref(false); // To disable button during fade-out
    const showRequestDrawer = ref(false);
    watch(password, () => {
      error.value && (error.value = "");
    });

    /**
     * Checks if the entered password is correct.
     * If correct, initiates fade-out animation and emits 'authenticated'.
     * If incorrect, displays an error message.
     */
    function checkPassword() {
      if (password.value === correctPassword) {
        fadeOutPassword();
      } else {
        showError("Wrong password. Please try again.");
        setTimeout(() => {
          error.value = "";
        }, 3000); // 3 s then hide
      }
    }

    /**
     * Displays the error message with a smooth animation.
     * @param {string} message - The error message to display.
     */
    function showError(message) {
      error.value = message;
      // Optional: Hide error after a certain time
      // setTimeout(() => { error.value = ""; }, 3000);
    }

    /**
     * Applies the fade-out animation and emits the 'authenticated' event after the animation completes.
     */
    function fadeOutPassword() {
      isFadingOut.value = true; // Disable the submit button
      const element = passwordWrapper.value;
      element.classList.add("fade-out");
      setTimeout(() => {
        emit("authenticated");
      }, 1000); // Duration matches the CSS transition duration
    }

    /**
     * Called when the request form is submitted successfully
     */
    function onRequestSubmitted() {
      // Close the drawer after a delay to allow the user to see the success message
      setTimeout(() => {
        showRequestDrawer.value = false;
      }, 3000);
    }

    onMounted(() => {
      if (passwordInput.value) {
        passwordInput.value.focus(); // Automatically focus the input on mount
      }
    });

    const passwordVisible = ref(false);

    function togglePassword() {
      passwordVisible.value = !passwordVisible.value;
    }

    return {
      password,
      checkPassword,
      passwordInput,
      passwordWrapper,
      error,
      showError,
      isFadingOut,
      passwordVisible,
      togglePassword,
      viewIconPath,
      hideIconPath,
      showRequestDrawer,
      onRequestSubmitted,
    };
  },
};
</script>

<style scoped>
.password-wrapper {
  position: relative;
  width: 100%;
  min-height: 100vh;
  height: auto;
  opacity: 1;
  transition: opacity 1s ease;
  display: flex;
  gap: 3vh;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: var(--largeSpace) var(--space);
  padding-bottom: calc(var(--largeSpace) + 80px); /* Space for footer */
  box-sizing: border-box;
}

/* Homepage title - matches main long read typography */
.homepage-title {
  position: relative;
  display: inline-block;
  font-size: 3rem;
  line-height: 1;
  padding-right: 6ch; /* Space for the offset "33 Path" */
}

.title-lower {
  position: absolute;
  transform: translateY(20px);
  white-space: nowrap;
}

@media (min-width: 768px) {
  .homepage-title {
    font-size: 4.5rem;
  }

  .title-lower {
    transform: translateY(30px);
  }
}

.logoPath {
  position: absolute;
  top: var(--clickableHeight);
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  color: var(--text-white); /* Ensure the logo text is visible */
}

/* Experience note styling */
.experience-note {
  color: var(--grey);
  text-align: center;
  color: var(--text-white);
  opacity: 0.8;
  margin-top: var(--space);
  max-width: 80%;
  margin-left: auto;
  margin-right: auto;
}

.password-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: var(--largeSpace);
}

.password-field {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: var(--space);
}

.password-input {
  width: 250px;
  padding-right: 2.5rem; /* space for toggle icon */
}

.toggle-btn {
  position: absolute;
  right: 0.5rem;
  top: calc(50% - calc(var(--space) / 2));
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  right: var(--largeSpace);
}

.password-icon {
  width: 31px;
  height: 31px;
  fill: var(--text-white);
}

.submit-button {
  padding: 10px 20px;
  font-size: 1.2rem;
  border: 1px solid var(--text-white);
  background-color: transparent;
  color: var(--text-white);
  outline: none;
  cursor: pointer;
  border-radius: var(--border-radius-pill);
  transition: background 0.3s ease, opacity 0.3s ease;
  margin-bottom: var(--space);
}

.submit-button:hover:not(:disabled) {
  background: var(--button-hover-background);
}

.submit-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.error-message {
  color: var(--red);
  margin-bottom: 10px;
  font-size: 1rem;
  /* Animation */
  opacity: 0;
  animation: fadeIn 0.5s forwards;
}

/* Request button container */
.request-button-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-top: var(--space);
  margin-bottom: var(--space);
}

/* Request access button styling */
.request-access-button {
  background-color: transparent;
  color: var(--text-white);
  outline: none;
  cursor: pointer;
  transition: background 0.3s ease, opacity 0.3s ease;
  /* In-flow button; no transform adjustments */
}

.request-access-button:hover {
  background: var(--button-hover-background);
}

/* Intro note styling */
.intro-note {
  color: var(--grey);
  text-align: center;
  font-size: 0.75rem;
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.4;
  opacity: 0.75;
}

.intro-note:first-of-type {
  margin-top: var(--smallSpace);
}

.intro-note:last-of-type {
  margin-bottom: var(--largeSpace);
}

.instagram-link {
  color: var(--purple-light);
  text-decoration: none;
  transition: color 0.2s ease;
}

.instagram-link:hover {
  color: var(--text-white);
  text-decoration: underline;
}

/* Transition for error message */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.fade-out {
  opacity: 0;
  transition: opacity 1s ease;
}

/* Footer positioned at absolute bottom */
.password-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding-bottom: var(--space);
}
</style>
